/**
 * Minimal OTLP/HTTP JSON contract for the ELF (Enterprise Logging Framework)
 * logs endpoint. This is a hand-written subset of the OpenTelemetry logs data
 * model so the browser can emit ELF-compatible logs without pulling in the full
 * @opentelemetry browser SDK.
 *
 * Ported verbatim from one-ap-customer_web's core/logging/elf-log.model.ts.
 *
 * The resource attributes below (service.name, service.namespace, service.car.id,
 * service.instance.id, deployment.environment) are the standard OTLP/ELF keys the
 * ingest endpoint expects.
 */

export enum ElfSeverity {
  TRACE = 'TRACE',
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
  FATAL = 'FATAL'
}

/** OpenTelemetry severity numbers per the logs data model spec. */
export const ELF_SEVERITY_NUMBER: Record<ElfSeverity, number> = {
  [ElfSeverity.TRACE]: 1,
  [ElfSeverity.DEBUG]: 5,
  [ElfSeverity.INFO]: 9,
  [ElfSeverity.WARN]: 13,
  [ElfSeverity.ERROR]: 17,
  [ElfSeverity.FATAL]: 21
};

export type ElfAttributeValue = string | number | boolean | null | undefined;
export type ElfAttributes = Record<string, ElfAttributeValue>;

/** Internal buffered log entry before it is serialized to OTLP. */
export interface ElfLogEntry {
  timeUnixNano: string;
  severity: ElfSeverity;
  body: string;
  attributes: ElfAttributes;
}

export interface OtlpAnyValue {
  stringValue?: string;
  boolValue?: boolean;
  intValue?: string;
  doubleValue?: number;
}

export interface OtlpKeyValue {
  key: string;
  value: OtlpAnyValue;
}

export interface OtlpLogRecord {
  timeUnixNano: string;
  observedTimeUnixNano: string;
  severityNumber: number;
  severityText: string;
  body: OtlpAnyValue;
  attributes: OtlpKeyValue[];
}

export interface OtlpScopeLogs {
  scope: { name: string; version?: string };
  logRecords: OtlpLogRecord[];
}

export interface OtlpResourceLogs {
  resource: { attributes: OtlpKeyValue[] };
  scopeLogs: OtlpScopeLogs[];
}

export interface OtlpLogsPayload {
  resourceLogs: OtlpResourceLogs[];
}

/** Shape of the ELF logger configuration block. */
export interface ElfLoggerConfig {
  enabled: boolean;
  /** Retained for parity with one-ap-customer_web; the logger POSTs directly to the resolved elfingest host. */
  logsEndpoint: string;
  serviceName: string;
  serviceNamespace: string;
  carId: string;
  scopeName: string;
  scopeVersion?: string;
  /** Flush the buffer once this many records have accumulated. */
  batchSize: number;
  /** Flush the buffer at least this often (ms) while records are pending. */
  flushIntervalMs: number;
}
